// ============================================================
// HOUSE OF CARDS — HotspotArt v1 — THE INKED LIBRARY
// One hand-drawn SVG per hotspot id, plus DoorSprite for the
// hall corridor. Everything is built from the RM palette so the
// rooms read as one illustrated world. No external images —
// when Midjourney plates land (Phase 6) they layer on top via
// `art:` urls; these sprites are the floor, not the ceiling.
//
// Every export is a component: ({ flags }) => <svg/>. Most ignore
// flags; a few (crib, music_box, icebox, wardrobe) react to state.
// Sizing: each sprite draws to its own viewBox; the room scales
// them by depth. Drop-shadows come from CSS (.hotspot-obj svg).
// ============================================================
import React from "react";
import { RM } from "./theme";

// shared ink tokens
const INK = RM.charBorder;
const INK2 = "#1c120d";
const WOOD = "#2a1a12";
const WOOD2 = "#3a261a";
const WOODHI = "#4d3220";

// tiny helpers ------------------------------------------------
const Defs = ({ id, from, to, x1 = 0, y1 = 0, x2 = 0, y2 = 1 }) => (
  <defs>
    <linearGradient id={id} x1={x1} y1={y1} x2={x2} y2={y2}>
      <stop offset="0" stopColor={from} />
      <stop offset="1" stopColor={to} />
    </linearGradient>
  </defs>
);
const Glow = ({ id, color, stop = 0.6 }) => (
  <defs>
    <radialGradient id={id} cx="50%" cy="50%" r="50%">
      <stop offset="0" stopColor={color} stopOpacity="0.95" />
      <stop offset={stop} stopColor={color} stopOpacity="0.35" />
      <stop offset="1" stopColor={color} stopOpacity="0" />
    </radialGradient>
  </defs>
);
const wrap = (w, h, children) => (
  <svg viewBox={`0 0 ${w} ${h}`} width={w} height={h} className="sprite" xmlns="http://www.w3.org/2000/svg">
    {children}
  </svg>
);

// ── DOOR (hall corridor) ─────────────────────────────────────
// Arched door, knob glows mold-green when open, sealed = boarded.
export function DoorSprite({ open, sealed }) {
  const knob = open ? RM.moldBright : RM.boneFaint;
  return wrap(96, 150, (
    <>
      <Defs id="doorWood" from={WOOD2} to="#160d09" />
      {open && <Glow id="doorOpen" color={RM.moldBright} />}
      {/* frame */}
      <path d="M6 150 V44 A42 42 0 0 1 90 44 V150 Z" fill={INK2} stroke={INK} strokeWidth="2.5" />
      {/* leaf */}
      <path d="M14 146 V46 A34 34 0 0 1 82 46 V146 Z" fill="url(#doorWood)" stroke={INK} strokeWidth="2" />
      {/* center seam (double door) */}
      <line x1="48" y1="18" x2="48" y2="146" stroke={INK} strokeWidth="1.6" opacity="0.8" />
      {/* recessed panels */}
      <rect x="22" y="64" width="20" height="34" rx="3" fill="none" stroke={INK} strokeWidth="1.4" opacity="0.7" />
      <rect x="54" y="64" width="20" height="34" rx="3" fill="none" stroke={INK} strokeWidth="1.4" opacity="0.7" />
      <path d="M22 56 H42 M54 56 H74" stroke={INK} strokeWidth="1.2" opacity="0.5" />
      {sealed ? (
        <>
          {/* boards nailed across */}
          <rect x="6" y="70" width="84" height="13" rx="2" transform="rotate(-9 48 76)" fill={WOOD} stroke={INK} strokeWidth="1.6" />
          <rect x="6" y="98" width="84" height="13" rx="2" transform="rotate(7 48 104)" fill={WOOD} stroke={INK} strokeWidth="1.6" />
        </>
      ) : (
        <>
          {open && <circle cx="48" cy="92" r="20" fill="url(#doorOpen)" />}
          {/* twin knobs at the seam */}
          <circle cx="42" cy="100" r="3.6" fill={knob} stroke={INK} strokeWidth="1" />
          <circle cx="54" cy="100" r="3.6" fill={knob} stroke={INK} strokeWidth="1" />
        </>
      )}
    </>
  ));
}

// ── HALL ─────────────────────────────────────────────────────
const MouthDoor = () => wrap(180, 168, (
  <>
    <Glow id="mGlow" color={RM.fleshGlow} stop={0.55} />
    <Defs id="mWood" from="#1e100d" to="#0c0706" />
    <ellipse cx="90" cy="92" rx="86" ry="80" fill="url(#mGlow)" opacity="0.5" />
    {/* double arched doors grown together */}
    <path d="M14 166 V58 A76 50 0 0 1 166 58 V166 Z" fill="url(#mWood)" stroke={RM.vein} strokeWidth="3" />
    {/* skin-grain veins */}
    <path d="M40 150 Q56 96 44 56 M138 150 Q124 96 136 58 M90 164 V150" stroke={RM.vein} strokeWidth="1.4" fill="none" opacity="0.6" />
    {/* eyes */}
    <ellipse cx="60" cy="70" rx="11" ry="8" fill="#0a0605" stroke={RM.fleshGlow} strokeWidth="1.6" />
    <ellipse cx="120" cy="70" rx="11" ry="8" fill="#0a0605" stroke={RM.fleshGlow} strokeWidth="1.6" />
    <circle cx="62" cy="71" r="3.2" fill={RM.fleshGlow} />
    <circle cx="118" cy="71" r="3.2" fill={RM.fleshGlow} />
    {/* mouth: closed seam with six teeth hinted */}
    <path d="M44 112 Q90 104 136 112 Q90 132 44 112 Z" fill="#160a09" stroke={RM.fleshGlow} strokeWidth="2" />
    <path d="M58 110 V120 M72 109 V124 M86 108 V126 M104 108 V126 M118 109 V124 M132 110 V120" stroke={RM.bone} strokeWidth="2" opacity="0.7" />
    {/* center seam */}
    <line x1="90" y1="30" x2="90" y2="166" stroke={RM.vein} strokeWidth="1.4" opacity="0.5" />
  </>
));

const MouthDoorOpen = () => wrap(180, 168, (
  <>
    <Glow id="moGlow" color={RM.fleshGlow} stop={0.5} />
    <Defs id="moWood" from="#23130f" to="#0c0706" />
    <ellipse cx="90" cy="92" rx="88" ry="82" fill="url(#moGlow)" opacity="0.7" />
    <path d="M14 166 V58 A76 50 0 0 1 166 58 V166 Z" fill="url(#moWood)" stroke={RM.fleshGlow} strokeWidth="3" />
    {/* wide-open mouth */}
    <path d="M40 100 Q90 84 140 100 Q132 150 90 156 Q48 150 40 100 Z" fill="#0a0504" stroke={RM.fleshGlow} strokeWidth="2.4" />
    {/* six open teeth, top + bottom */}
    <path d="M52 100 l8 16 l8 -16 M84 98 l8 18 l8 -18 M116 100 l8 16 l8 -16" fill={RM.bone} stroke={INK} strokeWidth="1" />
    <path d="M60 150 l8 -14 l8 14 M92 152 l8 -16 l8 16" fill={RM.bone} stroke={INK} strokeWidth="1" />
    {/* drool */}
    <path d="M74 128 q-2 18 1 30 M110 126 q3 16 0 26" stroke={RM.moldBright} strokeWidth="2" fill="none" opacity="0.6" />
    {/* hungry eyes */}
    <ellipse cx="58" cy="66" rx="12" ry="7" fill="#0a0605" stroke={RM.fleshGlow} strokeWidth="1.6" />
    <ellipse cx="122" cy="66" rx="12" ry="7" fill="#0a0605" stroke={RM.fleshGlow} strokeWidth="1.6" />
    <circle cx="58" cy="66" r="3.4" fill={RM.fleshGlow} />
    <circle cx="122" cy="66" r="3.4" fill={RM.fleshGlow} />
  </>
));

const MouthDoorEaten = () => wrap(180, 168, (
  <>
    <Glow id="meGlow" color={RM.ember} stop={0.5} />
    <path d="M14 166 V58 A76 50 0 0 1 166 58 V166 Z" fill="#0a0605" stroke={RM.charBorder} strokeWidth="3" />
    {/* open doorway onto descending stairs */}
    <path d="M34 166 V70 A56 40 0 0 1 146 70 V166 Z" fill="url(#meGlow)" opacity="0.5" />
    <path d="M34 166 V70 A56 40 0 0 1 146 70 V166 Z" fill="#070403" stroke={RM.emberDim} strokeWidth="2" />
    {/* stairs going down */}
    <path d="M52 120 H128 M58 132 H122 M64 144 H116 M70 156 H110" stroke={RM.ember} strokeWidth="2.4" opacity="0.55" />
    <path d="M52 120 L58 132 M128 120 L122 132 M58 132 L64 144 M122 132 L116 144" stroke={RM.emberDim} strokeWidth="1.4" opacity="0.5" />
  </>
));

const HallMushrooms = () => wrap(120, 70, (
  <>
    <Glow id="shroomG" color={RM.moldBright} stop={0.5} />
    <ellipse cx="60" cy="58" rx="52" ry="12" fill="url(#shroomG)" opacity="0.35" />
    {[18, 38, 60, 82, 102].map((x, i) => {
      const h = [22, 30, 36, 28, 20][i];
      return (
        <g key={x}>
          <path d={`M${x} 60 q-3 -${h * 0.5} 0 -${h}`} stroke="#cdbfa0" strokeWidth="3" fill="none" />
          <path d={`M${x - 9} ${60 - h} q9 -10 18 0 q-9 6 -18 0 Z`} fill="#b9472f" stroke={INK} strokeWidth="1.2" />
          <circle cx={x - 3} cy={60 - h - 2} r="1.4" fill="#e8d8b8" opacity="0.7" />
        </g>
      );
    })}
  </>
));

// ── NURSERY ──────────────────────────────────────────────────
const Ball = () => wrap(70, 70, (
  <>
    <Defs id="ballG" from="#c4565a" to="#5c1f24" x1={0.2} y1={0.2} x2={0.8} y2={0.9} />
    <ellipse cx="35" cy="62" rx="20" ry="5" fill="#000" opacity="0.4" />
    <circle cx="35" cy="38" r="24" fill="url(#ballG)" stroke={INK} strokeWidth="2" />
    <path d="M16 30 Q35 22 54 30 M16 46 Q35 54 54 46" stroke="#3a1418" strokeWidth="1.4" fill="none" opacity="0.7" />
    <ellipse cx="27" cy="29" rx="6" ry="4" fill="#e89aa0" opacity="0.5" />
  </>
));

const Crib = ({ flags }) => wrap(150, 140, (
  <>
    <Defs id="cribG" from="#2c3a4a" to="#0c0f14" />
    <ellipse cx="75" cy="130" rx="62" ry="9" fill="#000" opacity="0.4" />
    {/* mattress + blanket lump */}
    <rect x="26" y="78" width="98" height="34" rx="6" fill="url(#cribG)" stroke={INK} strokeWidth="2" />
    <path d="M40 96 Q60 80 86 92 Q104 100 110 96" stroke="#7a8294" strokeWidth="2" fill="none" opacity="0.6" />
    {/* iron bars */}
    {Array.from({ length: 9 }).map((_, i) => (
      <line key={i} x1={30 + i * 11.5} y1="40" x2={30 + i * 11.5} y2="112" stroke="#5b6675" strokeWidth="2.4" />
    ))}
    <rect x="24" y="36" width="102" height="7" rx="3" fill="#6c7686" stroke={INK} strokeWidth="1.4" />
    {/* legs */}
    <rect x="28" y="112" width="7" height="20" fill="#414a57" />
    <rect x="115" y="112" width="7" height="20" fill="#414a57" />
  </>
));

const MusicBox = ({ flags }) => wrap(96, 90, (
  <>
    <Defs id="mbG" from="#3a2a14" to="#1a1208" />
    <Glow id="mbCres" color={RM.goldBright} stop={0.5} />
    <ellipse cx="48" cy="80" rx="40" ry="7" fill="#000" opacity="0.4" />
    <rect x="14" y="40" width="68" height="38" rx="5" fill="url(#mbG)" stroke={RM.gold} strokeWidth="2" />
    {/* inlaid lid */}
    <path d="M14 40 L24 26 H72 L82 40 Z" fill="#2a1d0e" stroke={RM.gold} strokeWidth="1.6" />
    <path d="M24 26 H72" stroke={RM.goldBright} strokeWidth="1" opacity="0.6" />
    {/* crescent winding hole */}
    <circle cx="48" cy="58" r="9" fill="url(#mbCres)" opacity="0.7" />
    <path d="M52 53 a6 6 0 1 0 0 11 a4.5 4.5 0 1 1 0 -11 Z" fill={RM.goldBright} stroke={INK} strokeWidth="0.8" />
    {/* bone inlay dots */}
    <circle cx="26" cy="52" r="2" fill={RM.bone} opacity="0.7" />
    <circle cx="70" cy="52" r="2" fill={RM.bone} opacity="0.7" />
  </>
));

const Dresser = () => wrap(120, 130, (
  <>
    <Defs id="drG" from={WOOD2} to="#160d09" />
    <ellipse cx="60" cy="122" rx="52" ry="8" fill="#000" opacity="0.4" />
    <rect x="16" y="20" width="88" height="100" rx="4" fill="url(#drG)" stroke={INK} strokeWidth="2" />
    {/* three drawers, bottom one cracked open */}
    <rect x="24" y="28" width="72" height="26" rx="3" fill="none" stroke={INK} strokeWidth="1.6" />
    <rect x="24" y="58" width="72" height="26" rx="3" fill="none" stroke={INK} strokeWidth="1.6" />
    <rect x="22" y="88" width="76" height="28" rx="3" fill="#0c0706" stroke={RM.ember} strokeWidth="1.6" />
    {/* pulls */}
    {[41, 71].map((cx) => (
      <g key={cx}>
        <circle cx={cx} cy="41" r="3" fill={WOODHI} />
        <circle cx={cx} cy="71" r="3" fill={WOODHI} />
      </g>
    ))}
    {/* the torn page peeking out */}
    <path d="M40 96 l30 -3 l2 14 l-30 4 Z" fill="#d8cbb3" stroke={INK} strokeWidth="1" opacity="0.85" />
    <path d="M44 100 H66 M44 104 H62" stroke={INK} strokeWidth="0.8" opacity="0.6" />
  </>
));

const Suitcase = () => wrap(120, 84, (
  <>
    <Defs id="scG" from="#4a2f1c" to="#241509" />
    <ellipse cx="60" cy="76" rx="50" ry="7" fill="#000" opacity="0.4" />
    <rect x="18" y="26" width="84" height="48" rx="6" fill="url(#scG)" stroke={INK} strokeWidth="2" />
    {/* straps */}
    <rect x="36" y="26" width="9" height="48" fill="#2a1a10" stroke={INK} strokeWidth="1" />
    <rect x="76" y="26" width="9" height="48" fill="#2a1a10" stroke={INK} strokeWidth="1" />
    <rect x="34" y="46" width="13" height="6" fill={RM.gold} opacity="0.8" />
    <rect x="74" y="46" width="13" height="6" fill={RM.gold} opacity="0.8" />
    {/* handle */}
    <path d="M50 26 q10 -14 20 0" stroke="#2a1a10" strokeWidth="4" fill="none" />
    {/* travel tag */}
    <path d="M88 34 l12 4 l-3 9 l-11 -3 Z" fill="#cdbfa0" stroke={INK} strokeWidth="0.8" />
  </>
));

// ── STUDY ────────────────────────────────────────────────────
const moonBook = (phase) => () => wrap(40, 96, (
  <>
    <Defs id={`bk${phase}`} from="#1a1a1f" to="#0a0a0d" />
    <rect x="8" y="6" width="24" height="84" rx="3" fill={`url(#bk${phase})`} stroke={INK} strokeWidth="1.6" />
    <rect x="8" y="6" width="5" height="84" fill="#000" opacity="0.4" />
    {/* moon phase on spine */}
    {phase === 0 && <circle cx="20" cy="48" r="9" fill="#15151a" stroke="#2a2a33" strokeWidth="1" />}
    {phase === 1 && (
      <g>
        <circle cx="20" cy="48" r="9" fill="#15151a" />
        <path d="M22 40 a9 9 0 0 1 0 16 a6 6 0 1 0 0 -16 Z" fill="#cdbfa0" opacity="0.8" />
      </g>
    )}
    {phase === 2 && (
      <g>
        <path d="M20 39 a9 9 0 0 1 0 18 Z" fill="#cdbfa0" opacity="0.85" />
        <path d="M20 39 a9 9 0 0 0 0 18 Z" fill="#15151a" />
      </g>
    )}
    {phase === 3 && <circle cx="20" cy="48" r="9" fill="#e8dcc0" stroke={RM.bone} strokeWidth="0.6" />}
  </>
));

const ShelfSlot = () => wrap(110, 70, (
  <>
    <Glow id="slotG" color={RM.goldBright} stop={0.5} />
    <rect x="10" y="10" width="90" height="52" rx="4" fill="#0a0706" stroke={WOOD2} strokeWidth="3" />
    <ellipse cx="55" cy="38" rx="30" ry="18" fill="url(#slotG)" opacity="0.5" />
    {/* moss bed + crescent key */}
    <path d="M28 50 q12 -6 24 0 q12 -5 28 1" stroke={RM.moldBright} strokeWidth="3" fill="none" opacity="0.7" />
    <g transform="translate(46 30)">
      <path d="M8 -8 a9 9 0 1 0 0 18 a6.5 6.5 0 1 1 0 -18 Z" fill={RM.goldBright} stroke={INK} strokeWidth="1" />
      <rect x="6" y="6" width="4" height="16" fill={RM.gold} />
      <rect x="6" y="18" width="9" height="3" fill={RM.gold} />
    </g>
  </>
));

const TinderBox = () => wrap(84, 64, (
  <>
    <Defs id="tbG" from="#5a4326" to="#2a1d0e" />
    <ellipse cx="42" cy="56" rx="34" ry="6" fill="#000" opacity="0.4" />
    <rect x="14" y="24" width="56" height="30" rx="4" fill="url(#tbG)" stroke={INK} strokeWidth="2" />
    <path d="M14 24 H70 L66 16 H18 Z" fill="#3a2a16" stroke={INK} strokeWidth="1.4" />
    <circle cx="42" cy="20" r="2" fill={WOODHI} />
    {/* flint spark */}
    <path d="M30 38 l8 -6 l4 8 l-7 5 Z" fill="#6b6b72" stroke={INK} strokeWidth="0.8" />
    <path d="M46 36 l6 -3 m-4 8 l7 -2" stroke={RM.emberBright} strokeWidth="1.6" />
  </>
));

const Desk = () => wrap(150, 110, (
  <>
    <Defs id="deskG" from={WOOD2} to="#1a1009" />
    <ellipse cx="75" cy="102" rx="64" ry="8" fill="#000" opacity="0.4" />
    {/* top */}
    <rect x="14" y="40" width="122" height="14" rx="3" fill="url(#deskG)" stroke={INK} strokeWidth="2" />
    {/* legs + drawer block */}
    <rect x="20" y="54" width="40" height="48" fill="#241509" stroke={INK} strokeWidth="1.6" />
    <rect x="126" y="54" width="8" height="48" fill="#241509" stroke={INK} strokeWidth="1.6" />
    <rect x="26" y="62" width="28" height="14" rx="2" fill="none" stroke={INK} strokeWidth="1.2" />
    <circle cx="40" cy="69" r="2" fill={WOODHI} />
    {/* ledger + inkwell */}
    <rect x="74" y="28" width="34" height="14" rx="1" fill="#3a2f1c" stroke={INK} strokeWidth="1.2" transform="rotate(-4 91 35)" />
    <rect x="78" y="24" width="34" height="14" rx="1" fill="#cdbfa0" stroke={INK} strokeWidth="1" transform="rotate(3 95 31)" />
    <circle cx="118" cy="38" r="5" fill="#0c1a14" stroke={RM.moldDim} strokeWidth="1.4" />
  </>
));

const CoveredPortrait = () => wrap(110, 130, (
  <>
    <Defs id="sheetG" from="#b9ad92" to="#6e6450" />
    <ellipse cx="55" cy="122" rx="44" ry="7" fill="#000" opacity="0.4" />
    {/* frame under sheet */}
    <rect x="20" y="16" width="70" height="100" rx="3" fill="#241509" stroke={INK} strokeWidth="2" />
    {/* draped sheet */}
    <path d="M14 20 Q55 6 96 20 L98 96 Q88 108 80 98 Q70 110 60 99 Q50 110 40 99 Q30 109 22 98 L14 92 Z"
      fill="url(#sheetG)" stroke={INK} strokeWidth="1.6" />
    <path d="M30 30 Q55 24 80 30" stroke="#5e5343" strokeWidth="1.2" fill="none" opacity="0.6" />
    {/* a sliver of eye peeking */}
    <path d="M52 70 q8 -5 16 0 q-8 4 -16 0 Z" fill="#0a0605" opacity="0.7" />
    <circle cx="60" cy="70" r="2" fill={RM.fleshGlow} opacity="0.8" />
  </>
));

// ── KITCHEN ──────────────────────────────────────────────────
const LaundryHoop = () => wrap(90, 96, (
  <>
    <ellipse cx="45" cy="40" rx="30" ry="30" fill="none" stroke="#6e5a3a" strokeWidth="5" />
    <ellipse cx="45" cy="40" rx="30" ry="30" fill="none" stroke={INK} strokeWidth="1.4" />
    {/* wicker weave */}
    <path d="M22 40 H68 M45 16 V64 M28 26 L62 56 M62 26 L28 56" stroke="#5a4326" strokeWidth="1.4" opacity="0.7" />
    {/* net remnants */}
    <path d="M30 56 q15 14 30 0" stroke="#8a7a5a" strokeWidth="1.4" fill="none" opacity="0.6" />
    {/* chalk free-throw line */}
    <path d="M14 84 H76" stroke="#cdbfa0" strokeWidth="2" strokeDasharray="6 5" opacity="0.7" />
  </>
));

const Stove = () => wrap(140, 120, (
  <>
    <Defs id="stoveG" from="#2a2f33" to="#0e1012" />
    <Glow id="simmer" color={RM.ember} stop={0.5} />
    <ellipse cx="70" cy="112" rx="58" ry="8" fill="#000" opacity="0.4" />
    <rect x="18" y="36" width="104" height="74" rx="5" fill="url(#stoveG)" stroke={INK} strokeWidth="2" />
    {/* cooktop */}
    <rect x="18" y="36" width="104" height="14" fill="#15171a" stroke={INK} strokeWidth="1.4" />
    {[36, 70, 104].map((cx) => (
      <circle key={cx} cx={cx} cy="43" r="5" fill="#0a0c0e" stroke="#3a3f44" strokeWidth="1.4" />
    ))}
    {/* simmering pot back-left */}
    <ellipse cx="52" cy="30" rx="22" ry="9" fill="url(#simmer)" opacity="0.4" />
    <path d="M34 28 h36 v10 a18 6 0 0 1 -36 0 Z" fill="#3a3f44" stroke={INK} strokeWidth="1.4" />
    <ellipse cx="52" cy="28" rx="18" ry="5" fill="#1a1d20" />
    <path d="M44 22 q4 -8 0 -14 M58 22 q-3 -7 1 -13" stroke={RM.boneDim} strokeWidth="1.4" fill="none" opacity="0.5" />
    {/* oven door */}
    <rect x="30" y="58" width="80" height="44" rx="4" fill="#0e1012" stroke="#3a3f44" strokeWidth="1.6" />
    <rect x="42" y="66" width="56" height="14" rx="2" fill="#05181a" stroke={RM.moldDim} strokeWidth="1.2" />
    <rect x="40" y="54" width="60" height="4" rx="2" fill="#4a4f54" />
  </>
));

const Icebox = ({ flags }) => wrap(110, 130, (
  <>
    <Defs id="iceG" from="#3a4750" to="#161c20" />
    <Glow id="frost" color="#9fd0e0" stop={0.5} />
    <ellipse cx="55" cy="122" rx="46" ry="8" fill="#000" opacity="0.4" />
    <rect x="16" y="14" width="78" height="106" rx="6" fill="url(#iceG)" stroke={INK} strokeWidth="2" />
    {/* doors */}
    <line x1="55" y1="14" x2="55" y2="120" stroke={INK} strokeWidth="1.6" />
    <rect x="22" y="22" width="28" height="90" rx="3" fill="none" stroke="#2a343a" strokeWidth="1.2" />
    <rect x="60" y="22" width="28" height="90" rx="3" fill="none" stroke="#2a343a" strokeWidth="1.2" />
    {/* latches */}
    <rect x="50" y="50" width="10" height="6" fill="#7a6a4a" stroke={INK} strokeWidth="0.8" />
    {/* frost creep */}
    <ellipse cx="55" cy="64" rx="30" ry="40" fill="url(#frost)" opacity="0.18" />
    <path d="M30 40 l6 6 m-6 0 l6 -6 M78 90 l6 6 m-6 0 l6 -6" stroke="#cfe6ef" strokeWidth="1.2" opacity="0.5" />
  </>
));

const Dumbwaiter = () => wrap(100, 120, (
  <>
    <Defs id="dwG" from="#2a1d12" to="#120c07" />
    <rect x="14" y="14" width="72" height="92" rx="4" fill="#0a0706" stroke={WOOD2} strokeWidth="3" />
    {/* the hatch car */}
    <rect x="24" y="40" width="52" height="40" rx="3" fill="url(#dwG)" stroke={INK} strokeWidth="2" />
    <line x1="24" y1="60" x2="76" y2="60" stroke={INK} strokeWidth="1.2" opacity="0.6" />
    {/* taut rope + empty spindle */}
    <line x1="50" y1="14" x2="50" y2="40" stroke="#7a6a4a" strokeWidth="2.5" />
    <circle cx="50" cy="92" r="6" fill="none" stroke={RM.ember} strokeWidth="2" />
    <circle cx="50" cy="92" r="1.6" fill={RM.ember} />
    {/* little bell */}
    <path d="M78 28 q6 0 6 6 v3 h-12 v-3 q0 -6 6 -6 Z" fill={RM.gold} stroke={INK} strokeWidth="1" />
    <circle cx="78" cy="39" r="1.6" fill={RM.gold} />
  </>
));

const PantryDoor = () => wrap(96, 130, (
  <>
    <Defs id="pdG" from={WOOD2} to="#160d09" />
    <rect x="14" y="10" width="68" height="112" rx="4" fill="url(#pdG)" stroke={INK} strokeWidth="2" />
    <rect x="24" y="22" width="48" height="40" rx="2" fill="none" stroke={INK} strokeWidth="1.2" opacity="0.6" />
    <rect x="24" y="70" width="48" height="40" rx="2" fill="none" stroke={INK} strokeWidth="1.2" opacity="0.6" />
    {/* new chain + new lock */}
    <path d="M20 60 q28 10 56 0" stroke="#8a8a92" strokeWidth="3" fill="none" />
    {Array.from({ length: 6 }).map((_, i) => (
      <ellipse key={i} cx={24 + i * 10} cy={60 + Math.sin(i) * 2} rx="3" ry="4.5" fill="none" stroke="#a8a8b0" strokeWidth="1.4" />
    ))}
    <rect x="40" y="64" width="16" height="16" rx="2" fill="#3a3f44" stroke="#a8a8b0" strokeWidth="1.4" />
    <circle cx="48" cy="70" r="2" fill="#15171a" />
    {/* cold air leak */}
    <path d="M22 120 q10 6 20 0 q10 6 20 0" stroke="#9fd0e0" strokeWidth="1.2" fill="none" opacity="0.4" />
  </>
));

// ── BATHROOM ─────────────────────────────────────────────────
const Mirror = () => wrap(96, 120, (
  <>
    <Defs id="mirG" from="#1c2628" to="#0a1012" />
    <rect x="14" y="10" width="68" height="100" rx="30" fill="url(#mirG)" stroke="#3a2a16" strokeWidth="3" />
    {/* fog + appearing letters */}
    <rect x="22" y="20" width="52" height="80" rx="24" fill="#2a3638" opacity="0.5" />
    <path d="M34 44 H62 M34 54 H58 M34 64 H64 M34 74 H54" stroke="#cdbfa0" strokeWidth="1.6" opacity="0.45" />
    {/* a single finger-drawn streak */}
    <path d="M40 30 q12 8 0 16" stroke="#e8dcc0" strokeWidth="1.4" fill="none" opacity="0.6" />
  </>
));

const Tub = ({ flags }) => wrap(150, 90, (
  <>
    <Defs id="tubG" from="#dcdce4" to="#8a8a92" />
    <ellipse cx="75" cy="82" rx="62" ry="8" fill="#000" opacity="0.4" />
    <path d="M20 36 q-6 40 18 44 h74 q24 -4 18 -44 Z" fill="url(#tubG)" stroke={INK} strokeWidth="2" />
    {/* black water */}
    <ellipse cx="75" cy="40" rx="56" ry="12" fill="#05080a" stroke="#1a2024" strokeWidth="1.4" />
    <ellipse cx="75" cy="38" rx="40" ry="7" fill="#0a1014" opacity="0.8" />
    {/* faint glint deep down */}
    <circle cx="86" cy="42" r="2" fill={RM.bone} opacity="0.4" />
    {/* clawfeet */}
    <path d="M30 80 q-6 6 -2 10 M120 80 q6 6 2 10" stroke="#6a6a72" strokeWidth="4" fill="none" />
  </>
));

const TubEmpty = () => wrap(150, 90, (
  <>
    <Defs id="tubEG" from="#cdcdd6" to="#7a7a82" />
    <ellipse cx="75" cy="82" rx="62" ry="8" fill="#000" opacity="0.4" />
    <path d="M20 36 q-6 40 18 44 h74 q24 -4 18 -44 Z" fill="url(#tubEG)" stroke={INK} strokeWidth="2" />
    {/* dry interior + drain cross */}
    <ellipse cx="75" cy="40" rx="56" ry="12" fill="#1a1d20" />
    <ellipse cx="75" cy="58" rx="34" ry="9" fill="#0e1012" opacity="0.7" />
    <circle cx="75" cy="58" r="5" fill="#05080a" stroke="#3a3f44" strokeWidth="1.4" />
    <path d="M72 58 H78 M75 55 V61" stroke="#5a5f64" strokeWidth="1" />
    <path d="M30 80 q-6 6 -2 10 M120 80 q6 6 2 10" stroke="#6a6a72" strokeWidth="4" fill="none" />
  </>
));

const DrainChain = () => wrap(70, 110, (
  <>
    {/* bolt plate */}
    <rect x="20" y="10" width="30" height="14" rx="3" fill="#2a2f33" stroke={INK} strokeWidth="1.6" />
    <circle cx="27" cy="17" r="1.6" fill="#6a6f74" />
    <circle cx="43" cy="17" r="1.6" fill="#6a6f74" />
    {/* hanging chain */}
    {Array.from({ length: 9 }).map((_, i) => (
      <ellipse key={i} cx={35 + Math.sin(i / 1.5) * 3} cy={28 + i * 9} rx="4" ry="6" fill="none" stroke="#9a9aa2" strokeWidth="1.8" />
    ))}
    {/* pull knob */}
    <circle cx="35" cy="104" r="6" fill="#3a3f44" stroke="#9a9aa2" strokeWidth="1.4" />
  </>
));

const DrainCrank = () => wrap(96, 64, (
  <>
    <Defs id="crankG" from="#4a4f54" to="#1a1d20" />
    {/* iron crank handle */}
    <rect x="18" y="28" width="50" height="9" rx="4" fill="url(#crankG)" stroke={INK} strokeWidth="1.6" />
    <circle cx="20" cy="32" r="9" fill="none" stroke="#5a5f64" strokeWidth="4" />
    <rect x="62" y="20" width="9" height="26" rx="3" fill="url(#crankG)" stroke={INK} strokeWidth="1.4" />
    <circle cx="66" cy="16" r="6" fill="#3a3f44" stroke="#6a6f74" strokeWidth="1.4" />
    {/* rust */}
    <path d="M30 30 l4 4 m-2 -6 l5 3" stroke={RM.ember} strokeWidth="1.2" opacity="0.6" />
  </>
));

const DrainLocket = () => wrap(60, 70, (
  <>
    <Glow id="lockG" color={RM.bone} stop={0.5} />
    <ellipse cx="30" cy="44" rx="20" ry="14" fill="url(#lockG)" opacity="0.3" />
    {/* snapped chain */}
    <path d="M12 18 q8 -8 18 -4 q12 4 18 -6" stroke="#8a8a92" strokeWidth="1.6" fill="none" opacity="0.7" />
    {/* locket */}
    <circle cx="30" cy="42" r="13" fill="#9a9082" stroke={INK} strokeWidth="1.6" />
    <circle cx="30" cy="42" r="13" fill="none" stroke="#6a6256" strokeWidth="1" opacity="0.6" />
    <path d="M30 30 v24 M22 36 h16" stroke="#6a6256" strokeWidth="0.8" opacity="0.5" />
    <circle cx="30" cy="30" r="2.4" fill="none" stroke="#8a8a92" strokeWidth="1.4" />
  </>
));

const MedicineCabinet = () => wrap(96, 110, (
  <>
    <Defs id="medG" from="#1c2628" to="#0a1012" />
    <rect x="16" y="12" width="64" height="86" rx="4" fill="#0e1416" stroke="#3a2a16" strokeWidth="2.5" />
    {/* door ajar */}
    <rect x="48" y="14" width="34" height="82" rx="3" fill="url(#medG)" stroke={INK} strokeWidth="1.6" transform="skewY(-3)" />
    {/* shelves + jars */}
    <path d="M20 44 H46 M20 70 H46" stroke="#2a343a" strokeWidth="1.2" />
    <rect x="24" y="30" width="8" height="12" rx="2" fill="#5a3a1a" opacity="0.8" />
    <rect x="34" y="32" width="7" height="10" rx="2" fill="#6a6f74" opacity="0.7" />
    {/* the clean teeth jar */}
    <rect x="24" y="56" width="11" height="12" rx="2" fill="#cdbfa0" stroke={INK} strokeWidth="1" />
    <path d="M26 60 l2 3 l2 -3 l2 3 l2 -3" stroke="#9a9082" strokeWidth="1" fill="none" />
  </>
));

// ── MASTER BEDROOM ───────────────────────────────────────────
const Clock = ({ flags }) => wrap(80, 150, (
  <>
    <Defs id="clkG" from={WOOD2} to="#160d09" />
    <ellipse cx="40" cy="142" rx="32" ry="7" fill="#000" opacity="0.4" />
    <path d="M14 146 V30 q0 -16 26 -16 q26 0 26 16 V146 Z" fill="url(#clkG)" stroke={INK} strokeWidth="2" />
    {/* face */}
    <circle cx="40" cy="40" r="16" fill="#d8cbb3" stroke={INK} strokeWidth="1.6" />
    <circle cx="40" cy="40" r="1.6" fill={INK} />
    <path d="M40 40 V30 M40 40 L48 44" stroke={INK} strokeWidth="1.6" />
    {[0, 90, 180, 270].map((a) => (
      <line key={a} x1={40 + 13 * Math.cos((a * Math.PI) / 180)} y1={40 + 13 * Math.sin((a * Math.PI) / 180)}
        x2={40 + 15 * Math.cos((a * Math.PI) / 180)} y2={40 + 15 * Math.sin((a * Math.PI) / 180)} stroke={INK} strokeWidth="1" />
    ))}
    {/* glass case + still pendulum */}
    <rect x="26" y="64" width="28" height="74" rx="3" fill="#0a0605" stroke={WOODHI} strokeWidth="1.4" opacity="0.9" />
    <line x1="40" y1="66" x2="40" y2="118" stroke="#8a6a3a" strokeWidth="1.6" />
    <circle cx="40" cy="122" r="9" fill={RM.gold} stroke={INK} strokeWidth="1.4" />
  </>
));

const ClockOpen = () => wrap(80, 150, (
  <>
    <Glow id="ckoG" color={RM.gold} stop={0.5} />
    <Defs id="clkOG" from={WOOD2} to="#160d09" />
    <path d="M14 146 V30 q0 -16 26 -16 q26 0 26 16 V146 Z" fill="url(#clkOG)" stroke={INK} strokeWidth="2" />
    <circle cx="40" cy="40" r="16" fill="#b8ac90" stroke={INK} strokeWidth="1.6" />
    {/* open case showing empty nail + velvet pouch */}
    <rect x="22" y="62" width="36" height="78" rx="3" fill="#0a0605" stroke={WOODHI} strokeWidth="1.4" transform="rotate(-7 40 100)" />
    <ellipse cx="46" cy="96" rx="18" ry="22" fill="url(#ckoG)" opacity="0.4" />
    <circle cx="44" cy="78" r="1.8" fill="#6a6f74" />
    <path d="M44 78 q-8 14 -2 30 q8 6 14 -2 q4 -16 -4 -28 Z" fill="#3a1a2a" stroke={RM.vein} strokeWidth="1.2" opacity="0.85" />
  </>
));

const Fireplace = ({ flags }) => {
  const lit = flags && flags.fireplace_lit;
  return wrap(150, 130, (
    <>
      <Defs id="fpG" from="#2a201a" to="#120c09" />
      {lit && <Glow id="fpFire" color={RM.emberBright} stop={0.5} />}
      {/* mantle */}
      <rect x="10" y="22" width="130" height="14" rx="2" fill="#3a2a1c" stroke={INK} strokeWidth="2" />
      <rect x="18" y="36" width="114" height="86" fill="url(#fpG)" stroke={INK} strokeWidth="2" />
      {/* firebox */}
      <path d="M40 118 V64 q0 -10 35 -10 q35 0 35 10 V118 Z" fill="#0a0605" stroke="#2a1d12" strokeWidth="2" />
      {lit ? (
        <>
          <ellipse cx="75" cy="104" rx="34" ry="22" fill="url(#fpFire)" opacity="0.7" />
          <path d="M60 116 q-4 -22 10 -34 q-2 16 8 20 q6 -8 4 -18 q14 12 6 32 Z" fill={RM.ember} stroke={RM.emberBright} strokeWidth="1" />
          <path d="M68 116 q-2 -14 7 -22 q-1 10 5 13 q3 -5 2 -11 q8 8 3 20 Z" fill={RM.emberBright} />
        </>
      ) : (
        <>
          {/* stacked cold logs */}
          <rect x="52" y="98" width="46" height="9" rx="4" fill="#2a1a10" stroke={INK} strokeWidth="1" />
          <rect x="58" y="106" width="40" height="9" rx="4" fill="#241509" stroke={INK} strokeWidth="1" />
          <circle cx="54" cy="102" r="3" fill="#3a261a" />
        </>
      )}
    </>
  ));
};

const ScorchedWords = () => wrap(130, 80, (
  <>
    <Glow id="scoG" color={RM.ember} stop={0.5} />
    <rect x="8" y="8" width="114" height="64" rx="3" fill="#1a120c" opacity="0.5" />
    <ellipse cx="65" cy="40" rx="56" ry="28" fill="url(#scoG)" opacity="0.25" />
    {/* scorched handwriting hints */}
    <path d="M22 28 q14 -6 28 0 q10 4 20 -2 M22 44 q18 6 36 -2 q14 -4 26 2 M22 60 q12 -5 26 0 q14 5 30 -3"
      stroke={RM.ember} strokeWidth="2" fill="none" opacity="0.7" />
    <path d="M20 26 l4 4 M104 56 l4 4" stroke={RM.emberBright} strokeWidth="1.2" opacity="0.6" />
  </>
));

const PortraitBoys = () => wrap(110, 130, (
  <>
    <Defs id="pbFrame" from={RM.gold} to="#6a4f1a" />
    <ellipse cx="55" cy="122" rx="44" ry="7" fill="#000" opacity="0.4" />
    <rect x="16" y="12" width="78" height="106" rx="3" fill="url(#pbFrame)" stroke={INK} strokeWidth="2" />
    <rect x="24" y="20" width="62" height="90" fill="#1c1810" stroke={INK} strokeWidth="1.4" />
    {/* two boys */}
    <circle cx="44" cy="48" r="11" fill="#3a2e22" stroke={RM.boneDim} strokeWidth="1" />
    <path d="M33 92 q11 -28 22 0 Z" fill="#2a2018" stroke={RM.boneDim} strokeWidth="1" />
    <circle cx="68" cy="58" r="8" fill="#3a2e22" stroke={RM.boneDim} strokeWidth="1" />
    <path d="M60 92 q8 -22 16 0 Z" fill="#2a2018" stroke={RM.boneDim} strokeWidth="1" />
    {/* the locket, two brushstrokes */}
    <circle cx="68" cy="70" r="2.4" fill={RM.bone} opacity="0.85" />
    <path d="M64 66 q4 -3 8 0" stroke={RM.bone} strokeWidth="0.8" fill="none" opacity="0.7" />
  </>
));

const Wardrobe = ({ flags }) => wrap(120, 140, (
  <>
    <Defs id="wbG" from={WOOD2} to="#160d09" />
    <ellipse cx="60" cy="132" rx="50" ry="8" fill="#000" opacity="0.4" />
    <rect x="16" y="10" width="88" height="120" rx="4" fill="url(#wbG)" stroke={INK} strokeWidth="2" />
    {/* doors ajar — left slightly open */}
    <line x1="60" y1="14" x2="60" y2="126" stroke={INK} strokeWidth="1.6" />
    <rect x="22" y="16" width="34" height="108" rx="2" fill="#241509" stroke={INK} strokeWidth="1.2" transform="skewY(-1.5)" />
    <rect x="64" y="16" width="34" height="108" rx="2" fill="none" stroke={INK} strokeWidth="1.2" />
    {/* dark gap with breathing coats */}
    <rect x="50" y="20" width="12" height="100" fill="#050302" />
    <path d="M53 40 q3 18 0 40 q-2 18 1 34" stroke="#2a2018" strokeWidth="2" fill="none" opacity="0.7" />
    {/* knobs */}
    <circle cx="52" cy="68" r="3" fill={WOODHI} />
    <circle cx="68" cy="68" r="3" fill={WOODHI} />
  </>
));

const Bed = () => wrap(160, 110, (
  <>
    <Defs id="bedG" from="#2a2030" to="#120e16" />
    <ellipse cx="80" cy="104" rx="70" ry="8" fill="#000" opacity="0.4" />
    {/* headboard */}
    <rect x="18" y="20" width="14" height="80" rx="3" fill="#241509" stroke={INK} strokeWidth="2" />
    <rect x="128" y="40" width="12" height="60" rx="3" fill="#241509" stroke={INK} strokeWidth="2" />
    {/* mattress */}
    <rect x="24" y="58" width="112" height="34" rx="6" fill="url(#bedG)" stroke={INK} strokeWidth="2" />
    {/* hospital-corner sheet */}
    <path d="M24 70 H136 M30 92 q50 -8 100 0" stroke="#4a4458" strokeWidth="1.4" fill="none" opacity="0.6" />
    {/* two dented pillows */}
    <path d="M34 58 q14 -16 34 -2 q-2 8 -16 8 q-16 2 -18 -6 Z" fill="#3a3548" stroke={INK} strokeWidth="1.4" />
    <path d="M70 58 q14 -16 34 -2 q-2 8 -16 8 q-16 2 -18 -6 Z" fill="#3a3548" stroke={INK} strokeWidth="1.4" />
    <ellipse cx="50" cy="52" rx="6" ry="3" fill="#1e1b28" opacity="0.6" />
    <ellipse cx="86" cy="52" rx="6" ry="3" fill="#1e1b28" opacity="0.6" />
  </>
));

// ── CELLAR ───────────────────────────────────────────────────
const BurnedBeam = () => wrap(140, 90, (
  <>
    <Defs id="beamG" from="#1a120c" to="#070403" />
    {/* charred beam propped diagonally */}
    <g transform="rotate(-18 70 50)">
      <rect x="10" y="36" width="120" height="26" rx="4" fill="url(#beamG)" stroke="#0a0605" strokeWidth="2" />
      {/* alligator-char cracks */}
      <path d="M22 42 h14 v6 h-14 Z M44 50 h12 v6 h-12 Z M70 40 h14 v7 h-14 Z M96 50 h12 v6 h-12 Z"
        fill="none" stroke="#3a2418" strokeWidth="1" opacity="0.7" />
      {/* faint embered carving */}
      <path d="M30 56 q10 -3 18 0 M60 56 q12 3 24 -2" stroke={RM.ember} strokeWidth="1.4" fill="none" opacity="0.5" />
    </g>
    {/* glowing ember pinpoints */}
    <circle cx="50" cy="40" r="1.6" fill={RM.emberBright} opacity="0.7" />
    <circle cx="92" cy="58" r="1.4" fill={RM.ember} opacity="0.6" />
  </>
));

const Foundation = () => wrap(150, 100, (
  <>
    <Glow id="foundG" color={RM.flesh} stop={0.5} />
    <ellipse cx="75" cy="60" rx="64" ry="34" fill="url(#foundG)" opacity="0.22" />
    {/* blackened stone footprint */}
    {[
      [18, 40, 34, 16], [56, 38, 38, 18], [98, 42, 34, 16],
      [24, 60, 40, 18], [68, 62, 38, 16], [108, 60, 30, 18],
      [34, 82, 44, 16], [82, 82, 40, 16],
    ].map(([x, y, w, h], i) => (
      <rect key={i} x={x} y={y} width={w} height={h} rx="3" fill="#0e0907" stroke="#2a1a12" strokeWidth="1.4" />
    ))}
    {/* veins gathering down between stones */}
    <path d="M40 56 q6 14 0 30 M75 54 q-4 16 2 32 M110 58 q-4 14 -2 28" stroke={RM.vein} strokeWidth="1.6" fill="none" opacity="0.6" />
  </>
));

const PaintedRock = () => wrap(90, 70, (
  <>
    <Defs id="goldG" from={RM.goldBright} to="#7a5e1e" x1={0.2} y1={0.1} x2={0.8} y2={1} />
    <Glow id="goldGlow" color={RM.goldBright} stop={0.5} />
    <ellipse cx="45" cy="58" rx="34" ry="8" fill="#000" opacity="0.45" />
    <ellipse cx="45" cy="40" rx="40" ry="22" fill="url(#goldGlow)" opacity="0.35" />
    {/* gold bar half-buried */}
    <path d="M16 50 L24 34 H70 L78 50 Z" fill="url(#goldG)" stroke="#5a4316" strokeWidth="1.6" />
    <path d="M24 34 H70" stroke={RM.goldBright} strokeWidth="1.2" opacity="0.8" />
    <path d="M22 44 H72" stroke="#9a7a2e" strokeWidth="1" opacity="0.6" />
    {/* dirt mound over the base */}
    <path d="M10 50 q35 16 70 0 q-6 12 -35 12 q-29 0 -35 -12 Z" fill="#1a120c" stroke="#0a0605" strokeWidth="1" />
    <circle cx="34" cy="40" r="1.4" fill="#fff" opacity="0.6" />
  </>
));

const RootVeins = () => wrap(120, 130, (
  <>
    <Glow id="rootG" color={RM.fleshGlow} stop={0.5} />
    <ellipse cx="60" cy="70" rx="48" ry="56" fill="url(#rootG)" opacity="0.2" />
    {/* many veins converging into the dirt */}
    {Array.from({ length: 7 }).map((_, i) => {
      const x = 18 + i * 14;
      return (
        <path key={i} d={`M${x} 8 q${(60 - x) * 0.4} 40 ${(60 - x) * 0.1 + (60 - x) * 0.3} 70 Q60 108 60 124`}
          stroke={RM.vein} strokeWidth={1.4 + (i % 3) * 0.6} fill="none" opacity="0.7" />
      );
    })}
    {/* the gather-point pulsing into earth */}
    <ellipse cx="60" cy="120" rx="20" ry="8" fill="#1a0c10" stroke={RM.vein} strokeWidth="1.6" />
    <ellipse cx="60" cy="118" rx="10" ry="4" fill={RM.fleshDeep} opacity="0.7" />
  </>
));

// ── CHAPTER TWO — THE DESCENT (hotspot sprites) ──────────────
// Reactive to flags where it matters: the lantern lights, the
// small door opens, the cradle's lock appears, the hearth dims.

const Lantern = ({ flags = {} }) => {
  const lit = !!flags.lantern_lit;
  return wrap(80, 130, (
    <>
      {lit && <Glow id="lanGlow" color={RM.emberBright} stop={0.5} />}
      {lit && <ellipse cx="40" cy="78" rx="40" ry="46" fill="url(#lanGlow)" opacity="0.7" />}
      {/* hook + chain */}
      <path d="M40 4 v14 M40 6 a6 6 0 0 1 0 12" stroke={INK} strokeWidth="2.2" fill="none" />
      {/* top cap */}
      <path d="M24 30 H56 L50 22 H30 Z" fill="#2c2014" stroke={INK} strokeWidth="1.6" />
      {/* glass body */}
      <path d="M26 30 H54 V96 H26 Z" fill={lit ? "#3a2a12" : "#160f0a"} stroke={INK} strokeWidth="1.8" opacity="0.92" />
      {/* the flame */}
      {lit ? (
        <path d="M40 58 q-7 8 -3 18 q3 6 3 8 q0 -2 3 -8 q4 -10 -3 -18 Z" fill={RM.emberBright} stroke={RM.ember} strokeWidth="1" />
      ) : (
        <line x1="40" y1="70" x2="40" y2="84" stroke={RM.boneFaint} strokeWidth="2" />
      )}
      {/* base */}
      <path d="M22 96 H58 L54 110 H26 Z" fill="#2c2014" stroke={INK} strokeWidth="1.6" />
      {/* glass struts */}
      <line x1="40" y1="30" x2="40" y2="96" stroke={INK} strokeWidth="1" opacity="0.5" />
    </>
  ));
};

const MemoryDrawer = () => wrap(110, 90, (
  <>
    <Defs id="drwStone" from="#241710" to="#100a07" />
    {/* stone face */}
    <rect x="6" y="8" width="98" height="74" rx="4" fill="url(#drwStone)" stroke={INK} strokeWidth="2" />
    <path d="M14 18 H96 M14 30 H96" stroke={INK} strokeWidth="0.8" opacity="0.4" />
    {/* the open drawer */}
    <path d="M20 40 H90 L98 70 H12 Z" fill={WOOD2} stroke={INK} strokeWidth="1.8" />
    <rect x="18" y="44" width="74" height="6" fill="#1a110b" opacity="0.7" />
    {/* flannel + whistle inside */}
    <path d="M30 52 H80 L78 64 H32 Z" fill="#5a3a3a" stroke={INK} strokeWidth="0.8" opacity="0.7" />
    <rect x="48" y="53" width="16" height="5" rx="2.5" fill={RM.bone} stroke={INK} strokeWidth="0.8" />
    {/* pull */}
    <circle cx="55" cy="38" r="2.6" fill={RM.boneDim} stroke={INK} strokeWidth="1" />
  </>
));

const Passage = () => wrap(120, 140, (
  <>
    <Glow id="passG" color={RM.ember} stop={0.5} />
    {/* low arched passage */}
    <path d="M16 140 V64 A44 30 0 0 1 104 64 V140 Z" fill="#0a0605" stroke={RM.emberDim} strokeWidth="2.4" />
    <ellipse cx="60" cy="96" rx="40" ry="42" fill="url(#passG)" opacity="0.45" />
    {/* warm air / steam curls */}
    <path d="M44 110 q-6 -14 2 -26 M60 116 q6 -16 -2 -30 M76 110 q6 -12 -2 -24" stroke={RM.emberBright} strokeWidth="1.4" fill="none" opacity="0.5" />
    {/* threshold stones */}
    <path d="M16 132 H104 M24 140 V132 M48 140 V132 M72 140 V132 M96 140 V132" stroke={INK} strokeWidth="1.2" opacity="0.6" />
  </>
));

const SmallDoor = ({ flags = {} }) => {
  const open = !!flags.small_door_open;
  return wrap(100, 110, (
    <>
      {open && <Glow id="sdG" color={RM.emberBright} stop={0.5} />}
      {/* round door frame */}
      <circle cx="50" cy="62" r="44" fill={open ? "#0a0605" : "url(#sdWood)"} stroke={INK} strokeWidth="2.4" />
      <Defs id="sdWood" from={WOOD2} to="#160d09" />
      {open ? (
        <>
          <circle cx="50" cy="62" r="38" fill="url(#sdG)" opacity="0.5" />
          <circle cx="50" cy="62" r="38" fill="#070403" />
        </>
      ) : (
        <>
          {/* painted moons dark→light around the door */}
          {[0, 1, 2, 3].map((i) => {
            const a = (-60 + i * 40) * Math.PI / 180;
            const cx = 50 + Math.cos(a) * 26, cy = 62 + Math.sin(a) * 26;
            return <circle key={i} cx={cx} cy={cy} r={3 + i} fill={RM.boneDim} opacity={0.3 + i * 0.22} stroke={INK} strokeWidth="0.6" />;
          })}
          {/* whistle bore */}
          <circle cx="50" cy="50" r="3" fill="#0a0605" stroke={RM.boneFaint} strokeWidth="1.2" />
          {/* grain */}
          <path d="M30 62 q20 -8 40 0 M30 76 q20 8 40 0" stroke={INK} strokeWidth="1" fill="none" opacity="0.5" />
        </>
      )}
    </>
  ));
};

const Cradle = () => wrap(140, 110, (
  <>
    <Glow id="crG" color={RM.goldBright} stop={0.5} />
    <ellipse cx="70" cy="62" rx="60" ry="40" fill="url(#crG)" opacity="0.30" />
    {/* rockers */}
    <path d="M22 92 Q70 108 118 92" stroke={WOODHI} strokeWidth="4" fill="none" />
    {/* basket */}
    <path d="M30 56 Q70 70 110 56 L104 86 Q70 96 36 86 Z" fill={WOOD2} stroke={INK} strokeWidth="2" />
    {/* spindles */}
    {[38, 50, 62, 78, 90, 102].map((x) => (
      <line key={x} x1={x} y1="58" x2={x} y2="84" stroke={WOODHI} strokeWidth="1.6" opacity="0.8" />
    ))}
    {/* sleeping child: a small blanket mound + a curl */}
    <path d="M46 58 Q70 50 94 58 Q92 70 70 72 Q48 70 46 58 Z" fill="#d8cbb3" opacity="0.85" stroke={INK} strokeWidth="0.8" />
    <circle cx="84" cy="58" r="6" fill="#e8d8c0" stroke={INK} strokeWidth="0.8" />
    {/* half locket on a thread */}
    <path d="M84 62 v6" stroke={RM.gold} strokeWidth="1" />
    <path d="M82 68 a3 3 0 1 0 4 0 Z" fill={RM.goldBright} stroke={INK} strokeWidth="0.6" />
  </>
));

const LullabyBox = ({ flags = {} }) => {
  const whole = !!flags.child_stirred;
  return wrap(130, 120, (
    <>
      <Defs id="lbWood" from="#3a261a" to="#160d09" />
      {whole && <Glow id="lbG" color={RM.goldBright} stop={0.5} />}
      {whole && <ellipse cx="65" cy="60" rx="58" ry="52" fill="url(#lbG)" opacity="0.4" />}
      {/* trunk-sized box */}
      <rect x="14" y="40" width="102" height="64" rx="5" fill="url(#lbWood)" stroke={INK} strokeWidth="2" />
      {/* domed lid */}
      <path d="M14 40 Q65 14 116 40 Z" fill="#2a1a12" stroke={INK} strokeWidth="2" />
      {/* the cylinder of teeth, visible through a cutaway */}
      <rect x="28" y="54" width="74" height="34" rx="17" fill="#0a0605" stroke={RM.boneFaint} strokeWidth="1.4" />
      {/* teeth-pins: six gaps shown lighter if not yet whole */}
      {[34, 44, 54, 64, 74, 84, 94].map((x, i) => (
        <rect key={x} x={x} y="58" width="3" height="8"
          fill={whole ? RM.bone : (i % 2 ? RM.bone : RM.charBorder)}
          stroke={INK} strokeWidth="0.5" opacity={whole ? 1 : 0.8} />
      ))}
      {/* self-turning crank */}
      <circle cx="112" cy="78" r="6" fill="none" stroke={WOODHI} strokeWidth="2" />
      <line x1="112" y1="78" x2="118" y2="74" stroke={WOODHI} strokeWidth="2" />
    </>
  ));
};

const CradleLock = () => wrap(70, 80, (
  <>
    <Glow id="clG" color={RM.goldBright} stop={0.5} />
    <ellipse cx="35" cy="42" rx="30" ry="34" fill="url(#clG)" opacity="0.35" />
    {/* lock body shaped like a curled sleeping child */}
    <path d="M20 34 Q24 18 40 20 Q56 22 54 40 Q60 54 46 62 Q30 68 22 56 Q14 46 20 34 Z"
      fill="#b8923f" stroke={INK} strokeWidth="1.8" />
    {/* curl suggestion */}
    <path d="M40 32 Q50 36 46 48 Q40 56 30 52" stroke="#0a0605" strokeWidth="1.6" fill="none" opacity="0.6" />
    {/* keyhole */}
    <circle cx="36" cy="42" r="3.4" fill="#0a0605" />
    <rect x="34.5" y="44" width="3" height="8" fill="#0a0605" />
  </>
));

const HearthBelow = ({ flags = {} }) => {
  const robbed = !!flags.took_cradle_key;
  const c = robbed ? RM.emberDim : RM.emberBright;
  return wrap(110, 110, (
    <>
      <Glow id="hbG" color={c} stop={0.5} />
      <ellipse cx="55" cy="70" rx="44" ry="34" fill="url(#hbG)" opacity={robbed ? 0.3 : 0.6} />
      {/* hearth arch */}
      <path d="M16 104 V60 A39 34 0 0 1 94 60 V104 Z" fill="#1a110b" stroke={INK} strokeWidth="2.2" />
      <path d="M28 104 V66 A27 24 0 0 1 82 66 V104 Z" fill="#0a0605" stroke={RM.emberDim} strokeWidth="1.6" />
      {/* banked coals */}
      {[40, 50, 60, 70].map((x, i) => (
        <circle key={x} cx={x} cy={94 - (i % 2) * 4} r="4" fill={c} opacity={robbed ? 0.5 : 0.9} />
      ))}
      {/* the cradle key on a nail above */}
      <line x1="55" y1="40" x2="55" y2="30" stroke={INK} strokeWidth="1.4" />
      <path d="M50 28 Q53 20 60 24 Q66 28 62 36 Q56 40 52 34 Z" fill={RM.gold} stroke={INK} strokeWidth="1" opacity={robbed ? 0.25 : 1} />
    </>
  ));
};

// item icons for Chapter Two live in the INVENTORY ICONS section below
// (they must follow the `icon` helper definition).



// ── INVENTORY ICONS ──────────────────────────────────────────
// Small icons for the inventory tray, keyed by item id (parallel
// to HOTSPOT_ART). Colored by rarity logic from theme: keys gold,
// clues mold, tools ember, relics flesh, common bone. Rendered as
// <Icon/> at ~40px in the tray; they scale cleanly.
const icon = (children) => () => (
  <svg viewBox="0 0 48 48" width="40" height="40" className="item-icon" xmlns="http://www.w3.org/2000/svg">
    {children}
  </svg>
);

const RubberBallIcon = icon(
  <>
    <defs><radialGradient id="ibBall" cx="38%" cy="34%" r="70%">
      <stop offset="0" stopColor="#c4565a" /><stop offset="1" stopColor="#5c1f24" />
    </radialGradient></defs>
    <circle cx="24" cy="25" r="16" fill="url(#ibBall)" stroke={INK} strokeWidth="1.6" />
    <path d="M10 19 Q24 13 38 19 M10 31 Q24 37 38 31" stroke="#3a1418" strokeWidth="1.2" fill="none" opacity="0.7" />
    <ellipse cx="17" cy="18" rx="4" ry="2.6" fill="#e89aa0" opacity="0.5" />
  </>
);

const TornPageIcon = icon(
  <>
    <path d="M13 7 H33 L37 41 L11 43 Z" fill="#d8cbb3" stroke={INK} strokeWidth="1.4" />
    {/* torn top edge */}
    <path d="M13 7 l3 3 l-2 3 l4 2 l-2 3" stroke={INK} strokeWidth="1" fill="none" opacity="0.5" />
    <path d="M16 18 H32 M15 24 H30 M16 30 H33 M15 36 H27" stroke={RM.moldDim} strokeWidth="1.2" opacity="0.8" />
    <path d="M22 14 a4 4 0 1 1 4 0" stroke={RM.boneFaint} strokeWidth="1" fill="none" />
  </>
);

const keyIcon = (tone) => icon(
  <>
    <circle cx="16" cy="18" r="8" fill="none" stroke={tone} strokeWidth="3" />
    <circle cx="16" cy="18" r="3" fill="#0a0605" stroke={tone} strokeWidth="1" />
    <rect x="14.5" y="24" width="3" height="18" fill={tone} />
    <rect x="14.5" y="34" width="9" height="3" fill={tone} />
    <rect x="14.5" y="39" width="6" height="3" fill={tone} />
  </>
);

const CrescentKeyIcon = icon(
  <>
    <path d="M20 6 a13 13 0 1 0 0 26 a9 9 0 1 1 0 -26 Z" fill={RM.goldBright} stroke={INK} strokeWidth="1.4" />
    <rect x="17" y="30" width="4" height="14" fill={RM.gold} />
    <rect x="17" y="38" width="10" height="3" fill={RM.gold} />
  </>
);

const TinderBoxIcon = icon(
  <>
    <defs><linearGradient id="ibTin" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0" stopColor="#5a4326" /><stop offset="1" stopColor="#2a1d0e" />
    </linearGradient></defs>
    <rect x="9" y="18" width="30" height="20" rx="3" fill="url(#ibTin)" stroke={INK} strokeWidth="1.6" />
    <path d="M9 18 H39 L35 11 H13 Z" fill="#3a2a16" stroke={INK} strokeWidth="1.2" />
    <path d="M18 28 l5 -4 l3 6 l-5 3 Z" fill="#6b6b72" stroke={INK} strokeWidth="0.8" />
    <path d="M28 26 l5 -3 m-3 8 l6 -2" stroke={RM.emberBright} strokeWidth="1.6" />
  </>
);

const IceBlockIcon = icon(
  <>
    <defs><linearGradient id="ibIce" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0" stopColor="#add4e0" stopOpacity="0.85" /><stop offset="1" stopColor="#4a6f78" stopOpacity="0.9" />
    </linearGradient></defs>
    <path d="M12 14 L36 12 L38 36 L14 38 Z" fill="url(#ibIce)" stroke="#cfe6ef" strokeWidth="1.4" opacity="0.9" />
    <path d="M16 16 L34 30 M34 16 L18 34" stroke="#fff" strokeWidth="0.8" opacity="0.4" />
    {/* brass valve inside */}
    <circle cx="25" cy="25" r="6" fill="none" stroke={RM.gold} strokeWidth="2" opacity="0.85" />
    <circle cx="25" cy="25" r="2" fill={RM.goldBright} opacity="0.8" />
  </>
);

const BrassValveIcon = icon(
  <>
    <circle cx="24" cy="24" r="13" fill="none" stroke={RM.gold} strokeWidth="4" />
    <circle cx="24" cy="24" r="4" fill={RM.goldBright} stroke={INK} strokeWidth="1" />
    {[0, 72, 144, 216, 288].map((a) => (
      <line key={a} x1="24" y1="24"
        x2={24 + 13 * Math.cos((a * Math.PI) / 180)} y2={24 + 13 * Math.sin((a * Math.PI) / 180)}
        stroke={RM.gold} strokeWidth="2.4" />
    ))}
  </>
);

const IronCrankIcon = icon(
  <>
    <defs><linearGradient id="ibCrank" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0" stopColor="#4a4f54" /><stop offset="1" stopColor="#1a1d20" />
    </linearGradient></defs>
    <rect x="10" y="22" width="26" height="6" rx="3" fill="url(#ibCrank)" stroke={INK} strokeWidth="1.4" />
    <circle cx="11" cy="25" r="7" fill="none" stroke="#5a5f64" strokeWidth="3.4" />
    <rect x="31" y="14" width="6" height="20" rx="3" fill="url(#ibCrank)" stroke={INK} strokeWidth="1.2" />
    <circle cx="34" cy="11" r="4.5" fill="#3a3f44" stroke="#6a6f74" strokeWidth="1.2" />
    <path d="M18 24 l3 3 m-1 -5 l4 2" stroke={RM.ember} strokeWidth="1" opacity="0.6" />
  </>
);

const TarnishedLocketIcon = icon(
  <>
    <path d="M10 12 q8 -6 14 -3 q10 3 14 -5" stroke="#8a8a92" strokeWidth="1.6" fill="none" opacity="0.7" />
    <circle cx="24" cy="28" r="13" fill="#9a9082" stroke={INK} strokeWidth="1.6" />
    <circle cx="24" cy="28" r="13" fill="none" stroke="#6a6256" strokeWidth="1" opacity="0.6" />
    <path d="M24 15 v26 M11 28 h26" stroke="#6a6256" strokeWidth="0.8" opacity="0.5" />
    <circle cx="24" cy="15" r="2.4" fill="none" stroke="#8a8a92" strokeWidth="1.4" />
  </>
);

const PaintedRockIcon = icon(
  <>
    <defs><linearGradient id="ibGold" x1="0.2" y1="0.1" x2="0.8" y2="1">
      <stop offset="0" stopColor={RM.goldBright} /><stop offset="1" stopColor="#7a5e1e" />
    </linearGradient></defs>
    <path d="M8 30 L16 14 H38 L42 30 Z" fill="url(#ibGold)" stroke="#5a4316" strokeWidth="1.6" />
    <path d="M16 14 H38" stroke={RM.goldBright} strokeWidth="1.2" opacity="0.8" />
    <path d="M13 24 H40" stroke="#9a7a2e" strokeWidth="1" opacity="0.6" />
    {/* flecks of grey paint still clinging */}
    <circle cx="20" cy="22" r="1.4" fill="#8a8a92" opacity="0.7" />
    <circle cx="32" cy="20" r="1.2" fill="#8a8a92" opacity="0.6" />
  </>
);

// ── Chapter Two item icons ───────────────────────────────────
const EmberJarIcon = icon(
  <>
    <defs><radialGradient id="ibEmber" cx="50%" cy="62%" r="55%">
      <stop offset="0" stopColor={RM.emberBright} /><stop offset="1" stopColor={RM.emberDim} />
    </radialGradient></defs>
    <path d="M16 14 H32 V18 Q38 22 38 30 V42 H10 V30 Q10 22 16 18 Z" fill="#1a110b" stroke="#9fb8c4" strokeWidth="1.4" opacity="0.85" />
    <rect x="15" y="9" width="18" height="6" rx="2" fill="#3a2a16" stroke={INK} strokeWidth="1.2" />
    <circle cx="24" cy="32" r="8" fill="url(#ibEmber)" />
    <circle cx="24" cy="32" r="3" fill={RM.goldBright} />
  </>
);

const BoneWhistleIcon = icon(
  <>
    <path d="M10 22 H34 Q42 22 42 28 Q42 34 34 34 H10 Q6 34 6 28 Q6 22 10 22 Z" fill={RM.bone} stroke={INK} strokeWidth="1.4" />
    <circle cx="16" cy="28" r="2.4" fill="#0a0605" />
    <circle cx="24" cy="28" r="2.4" fill="#0a0605" />
    <circle cx="32" cy="28" r="2.4" fill="#0a0605" />
    <path d="M42 25 H46 V31 H42 Z" fill="#cdbfa0" stroke={INK} strokeWidth="1" />
  </>
);

const BrothersLocketIcon = icon(
  <>
    <defs><radialGradient id="ibBloc" cx="42%" cy="38%" r="65%">
      <stop offset="0" stopColor={RM.goldBright} /><stop offset="1" stopColor={RM.gold} />
    </radialGradient></defs>
    <path d="M24 10 a5 5 0 0 1 0 10" fill="none" stroke={RM.gold} strokeWidth="2" />
    <path d="M24 22 C16 14 8 20 12 28 C15 34 24 40 24 40 C24 40 33 34 36 28 C40 20 32 14 24 22 Z"
      fill="url(#ibBloc)" stroke={INK} strokeWidth="1.4" />
    <line x1="24" y1="22" x2="24" y2="40" stroke="#0a0605" strokeWidth="0.8" opacity="0.5" />
  </>
);

export const ITEM_ICONS = {
  rubber_ball: RubberBallIcon,
  torn_page: TornPageIcon,
  crescent_key: CrescentKeyIcon,
  rust_key: keyIcon(RM.ember),        // smells like the kitchen — warm iron
  tinder_box: TinderBoxIcon,
  ice_block: IceBlockIcon,
  brass_valve: BrassValveIcon,
  iron_crank: IronCrankIcon,
  tarnished_key: keyIcon(RM.boneDim), // dumbwaiter key, dull
  tarnished_locket: TarnishedLocketIcon,
  black_key: keyIcon("#3a3f44"),      // black iron
  painted_rock: PaintedRockIcon,
  // Chapter Two
  ember_jar: EmberJarIcon,
  bone_whistle: BoneWhistleIcon,
  cradle_key: keyIcon(RM.goldBright),
  brothers_locket: BrothersLocketIcon,
};

// ── REGISTRY ─────────────────────────────────────────────────
export const HOTSPOT_ART = {
  // hall
  mouth_door: MouthDoor,
  mouth_door_open: MouthDoorOpen,
  mouth_door_eaten: MouthDoorEaten,
  hall_mushrooms: HallMushrooms,
  // nursery
  ball: Ball,
  crib: Crib,
  music_box: MusicBox,
  dresser: Dresser,
  suitcase: Suitcase,
  // study
  book_new: moonBook(0),
  book_crescent: moonBook(1),
  book_half: moonBook(2),
  book_full: moonBook(3),
  shelf_slot: ShelfSlot,
  tinder_box_spot: TinderBox,
  desk: Desk,
  portrait: CoveredPortrait,
  // kitchen
  laundry_hoop: LaundryHoop,
  stove: Stove,
  icebox: Icebox,
  dumbwaiter: Dumbwaiter,
  pantry_door: PantryDoor,
  // bath
  mirror: Mirror,
  tub: Tub,
  tub_empty: TubEmpty,
  drain_chain: DrainChain,
  drain_crank: DrainCrank,
  drain_locket: DrainLocket,
  medicine_cabinet: MedicineCabinet,
  // master
  clock: Clock,
  clock_open: ClockOpen,
  fireplace: Fireplace,
  scorched_words: ScorchedWords,
  portrait_boys: PortraitBoys,
  wardrobe: Wardrobe,
  bed: Bed,
  // cellar
  burned_beam: BurnedBeam,
  foundation: Foundation,
  painted_rock_spot: PaintedRock,
  root_veins: RootVeins,
  // chapter two — the descent
  landing_lantern: Lantern,
  landing_lit: Lantern,
  landing_drawer: MemoryDrawer,
  long_passage: Passage,
  small_door: SmallDoor,
  small_door_open: SmallDoor,
  cradle_lock: CradleLock,
  the_cradle: Cradle,
  lullaby_box: LullabyBox,
  the_hearth_below: HearthBelow,
};
